import React from 'react';
import {
  Alert,
  Button,
  Card,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';

import { isSyntheticBondBatchTerminal, SyntheticBondBatchStatus } from 'state';

type Props = {
  batch: SyntheticBondBatchStatus;
  error?: string;
  fetching?: boolean;
  retryError?: string;
  retrying?: boolean;
  onRefresh: () => void;
  onRetry: () => void;
};

export const canRetrySyntheticBondBatch = (status: string) =>
  ['FAILED', 'PARTIAL_FAILURE', 'PARTIAL_FAILED', 'ENQUEUE_FAILED'].includes(
    status,
  );

const BatchProgress: React.FC<Props> = ({
  batch,
  error,
  fetching,
  onRefresh,
  onRetry,
  retryError,
  retrying,
}) => {
  const status = String(batch.status || 'QUEUED').toUpperCase();
  const items = batch.items || batch.results || [];
  const total = Number(
    batch.progress?.candidate_total || batch.total_count || items.length || 0,
  );
  const completed = Number(
    batch.progress?.completed_total ||
      batch.completed_count ||
      batch.existing_count ||
      items.filter(item =>
        [
          'COMPLETED',
          'CREATED',
          'EXISTING',
          'ALREADY_EXISTS',
          'SUCCESS',
        ].includes(String(item.status).toUpperCase()),
      ).length,
  );
  const failed = Number(
    batch.progress?.failed_total ||
      batch.failed_count ||
      items.filter(item => String(item.status).toUpperCase() === 'FAILED')
        .length,
  );
  const percent = total
    ? Math.min(100, Math.round((completed / total) * 100))
    : 0;
  const terminal = isSyntheticBondBatchTerminal(status);
  const terminalFailure = [
    'FAILED',
    'PARTIAL_FAILURE',
    'PARTIAL_FAILED',
    'ENQUEUE_FAILED',
    'STALE',
  ].includes(status);
  const canRetry = canRetrySyntheticBondBatch(status);

  return (
    <Card className="synthetic-bonds__progress" title="3. Creation progress">
      <div className="synthetic-bonds__progress-heading">
        <div>
          <Space>
            <Tag
              color={
                status === 'SUCCEEDED' || status === 'COMPLETED'
                  ? 'success'
                  : terminalFailure
                    ? 'error'
                    : 'processing'
              }
            >
              {status}
            </Tag>
            <Typography.Text copyable>
              {batch.batch_id || batch.id || batch.uuid}
            </Typography.Text>
          </Space>
          {batch.current_candidate && (
            <Typography.Paragraph type="secondary">
              Current candidate: {batch.current_candidate}
            </Typography.Paragraph>
          )}
        </div>
        <Space>
          <Button disabled={retrying} loading={fetching} onClick={onRefresh}>
            Refresh
          </Button>
          {canRetry && (
            <Button
              disabled={fetching}
              loading={retrying}
              onClick={onRetry}
              type="primary"
            >
              Retry safe failed items
            </Button>
          )}
        </Space>
      </div>

      <Progress
        percent={percent}
        status={
          terminalFailure || failed
            ? 'exception'
            : terminal
              ? 'success'
              : 'active'
        }
      />
      <Typography.Paragraph type="secondary">
        {completed.toLocaleString()} of {total.toLocaleString()} candidates
        complete; {failed.toLocaleString()} failed. Published rows are never
        overwritten by retry.
      </Typography.Paragraph>

      {error && (
        <Alert
          description={error}
          showIcon
          title="Status refresh failed; the server batch was not marked failed."
          type="warning"
        />
      )}
      {retryError && (
        <Alert
          description={retryError}
          showIcon
          title="Retry failed"
          type="error"
        />
      )}
      {status === 'STALE' && (
        <Alert
          description="Source inputs changed after review. Create a fresh preview; this batch cannot be retried with silently different prices."
          showIcon
          title="Reviewed input is stale"
          type="error"
        />
      )}
      {(status === 'SUCCEEDED' || status === 'COMPLETED') && (
        <Alert
          description="The assets now exist, but they have not been added to a session. Select them when configuring an ordinary historical session."
          showIcon
          title="Generation completed"
          type="success"
        />
      )}

      {items.length > 0 && (
        <Table
          className="synthetic-bonds__result-table"
          columns={[
            {
              title: 'Asset',
              key: 'asset',
              render: (_: unknown, item: any) =>
                item.result?.name ||
                item.identifiers?.name ||
                item.name ||
                item.ticker ||
                item.asset_name ||
                item.result?.underlying_asset_id ||
                item.asset_id ||
                '—',
            },
            {
              title: 'Ticker',
              key: 'ticker',
              render: (_: unknown, item: any) =>
                item.result?.ticker ||
                item.identifiers?.ticker ||
                item.ticker ||
                '—',
            },
            {
              title: 'ISIN',
              key: 'isin',
              render: (_: unknown, item: any) =>
                item.result?.isin || item.identifiers?.isin || item.isin || '—',
            },
            {
              title: 'Issue',
              key: 'issue',
              render: (_: unknown, item: any) =>
                item.legal_issue_date || item.issue_date || '—',
            },
            {
              title: 'Rows',
              key: 'rows',
              render: (_: unknown, item: any) =>
                item.result?.market_history_row_count ||
                item.row_count ||
                item.created_row_count ||
                item.expected_row_count ||
                0,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (value: unknown) => <Tag>{String(value || '—')}</Tag>,
            },
          ]}
          dataSource={items}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          rowKey={(item: any, index) =>
            item.batch_item_id || item.id || item.asset_id || String(index)
          }
          scroll={{ x: 800 }}
          size="small"
        />
      )}
    </Card>
  );
};

export default BatchProgress;
